import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, extractError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, Video, ShieldCheck, Loader2, Radio, Square, Upload } from "lucide-react";

function loadJitsiScript() {
  return new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) return resolve();
    const script = document.createElement("script");
    script.src = "https://meet.jit.si/external_api.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Jitsi"));
    document.body.appendChild(script);
  });
}

export default function Session() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [consent, setConsent] = useState(false);
  const [recordingConsent, setRecordingConsent] = useState(false);
  const [joined, setJoined] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recStatus, setRecStatus] = useState("idle"); // idle | recording | uploading | done
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const isSpecialist = user && (user.role === "clinical_specialist" || user.role === "adjunctive_specialist");

  const refreshBooking = async () => {
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setBooking(data);
    } catch (e) {
      toast.error(extractError(e, "Booking not found"));
      nav(isSpecialist ? "/specialist" : "/me");
    }
  };

  useEffect(() => { refreshBooking(); /* eslint-disable-next-line */ }, [id]);

  // Poll booking every 6s while on the page so both sides see recording state changes
  useEffect(() => {
    const t = setInterval(() => { refreshBooking(); }, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [id]);

  const requestJoin = async () => {
    if (!consent) return toast.error("Please give consent to continue");
    if (booking?.recording_requested && !isSpecialist && !recordingConsent) {
      return toast.error("This session is set to be recorded. Please consent below.");
    }
    setLoading(true);
    try {
      if (user?.role === "end_user") {
        await api.post(`/bookings/${id}/consent`);
        if (booking?.recording_requested && recordingConsent) {
          await api.post(`/bookings/${id}/recording/consent`);
        }
      }
      await loadJitsiScript();
      setJoined(true);
      await refreshBooking();
    } catch (e) {
      toast.error(e.message || "Could not start Jitsi session");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!joined || !booking) return;
    if (!containerRef.current || !window.JitsiMeetExternalAPI) return;
    if (apiRef.current) return;
    try {
      apiRef.current = new window.JitsiMeetExternalAPI("meet.jit.si", {
        roomName: booking.jitsi_room || `Manodayam-${id}`,
        parentNode: containerRef.current,
        width: "100%",
        height: "100%",
        userInfo: { displayName: user?.name || "Manodayam guest" },
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          prejoinPageEnabled: true,
          disableDeepLinking: true,
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK: false,
          DEFAULT_BACKGROUND: "#1a2f25",
        },
      });
      apiRef.current.addListener("readyToClose", () => {
        stopAndMaybeUpload(true);
        try { apiRef.current.dispose(); } catch {}
        apiRef.current = null;
        nav(isSpecialist ? "/specialist" : "/me");
      });
    } catch (e) {
      toast.error("Failed to start Jitsi session");
      setJoined(false);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [joined, booking, id]);

  useEffect(() => () => {
    if (apiRef.current) { try { apiRef.current.dispose(); } catch {} apiRef.current = null; }
    stopStream();
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    if (!booking?.recording_patient_consent) {
      return toast.error("Recording can only start after the patient consents.");
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 15 },
        audio: true,
      });
      // Also capture microphone so both voices are recorded
      let mixed = stream;
      try {
        const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const dest = ctx.createMediaStreamDestination();
        const s1 = ctx.createMediaStreamSource(stream);
        const s2 = ctx.createMediaStreamSource(mic);
        s1.connect(dest); s2.connect(dest);
        mixed = new MediaStream([...stream.getVideoTracks(), ...dest.stream.getAudioTracks()]);
        // ensure mic tracks are stopped when we stop
        stream.addEventListener("inactive", () => mic.getTracks().forEach((t) => t.stop()));
      } catch {}
      streamRef.current = stream;
      chunksRef.current = [];
      const mimeCandidates = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
      const mimeType = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) || "video/webm";
      const rec = new MediaRecorder(mixed, { mimeType, videoBitsPerSecond: 700_000 });
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => uploadRecording(mimeType);
      // Stop if the user cancels tab share from the browser UI
      stream.getVideoTracks()[0].addEventListener("ended", () => { if (rec.state === "recording") rec.stop(); });
      rec.start(1000);
      mediaRecorderRef.current = rec;
      setRecStatus("recording");
      toast.success("Recording started");
    } catch (e) {
      toast.error(e.message || "Could not start recording");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    stopStream();
  };

  const stopAndMaybeUpload = (silent = false) => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
    stopStream();
  };

  const uploadRecording = async (mimeType) => {
    if (!chunksRef.current.length) return;
    setRecStatus("uploading");
    const blob = new Blob(chunksRef.current, { type: mimeType });
    const fd = new FormData();
    fd.append("file", blob, `session-${id}.webm`);
    try {
      await api.post(`/bookings/${id}/recording/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Recording saved to booking");
      setRecStatus("done");
      refreshBooking();
    } catch (e) {
      toast.error(extractError(e, "Failed to upload recording"));
      setRecStatus("idle");
    }
  };

  if (!booking) return <div className="p-8 text-[color:var(--mano-ink-soft)] animate-pulse">Loading session…</div>;

  const showRecConsent = booking.recording_requested && !isSpecialist;

  return (
    <div className="min-h-screen bg-[color:var(--mano-bg)]" data-testid="session-page">
      <header className="px-5 lg:px-8 py-4 bg-white border-b border-[color:var(--mano-border)] flex items-center justify-between">
        <Link to={isSpecialist ? "/specialist" : "/me"} className="inline-flex items-center gap-1 uppercase-label" data-testid="session-back">
          <ChevronLeft size={14} /> Back
        </Link>
        <div className="font-display text-sm">Room: <span className="font-mono">{booking.jitsi_room}</span></div>
      </header>

      {/* Waiting-room card — hidden once joined */}
      <div className={joined ? "hidden" : "block"}>
        <div className="max-w-2xl mx-auto px-5 py-10">
          <div className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-7 lg:p-9">
            <span className="uppercase-label">Session · Waiting room</span>
            <h1 className="font-display text-2xl mt-2">
              {!isSpecialist ? `Consult with ${booking.expert_name}` : "You're about to join the consult"}
            </h1>
            <div className="mt-4 flex items-center gap-2 text-[color:var(--mano-ink-soft)] text-sm">
              <div><b>{new Date(booking.start_iso).toLocaleString()}</b></div>
              <span>·</span>
              <div>{booking.session_type}</div>
            </div>

            <div className="mt-6 rounded-2xl border border-[color:var(--mano-border)] p-4 flex items-start gap-3">
              <ShieldCheck size={18} className="text-[color:var(--mano-primary)] shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold">Before you join</div>
                <ul className="list-disc pl-4 mt-1 text-[color:var(--mano-ink-soft)] space-y-1">
                  <li>Please be in a quiet, private space.</li>
                  <li>You may leave the session anytime.</li>
                </ul>
              </div>
            </div>

            {showRecConsent && (
              <div className="mt-4 rounded-2xl border-2 border-[color:var(--mano-mild)] bg-[color:var(--mano-mild)]/10 p-4" data-testid="rec-consent-block">
                <div className="flex items-start gap-3">
                  <Radio size={18} className="text-[color:var(--mano-mild)] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">This session is set to be recorded</div>
                    <div className="text-sm text-[color:var(--mano-ink-soft)] mt-1">
                      Your specialist has requested to record this consult for clinical audit and continuity.
                      The clip is stored securely against your booking and only accessible to you, your specialist, and the platform admin.
                    </div>
                    <label className="mt-3 flex items-start gap-3 cursor-pointer">
                      <input
                        data-testid="session-rec-consent"
                        type="checkbox"
                        checked={recordingConsent}
                        onChange={(e) => setRecordingConsent(e.target.checked)}
                        className="mt-1 h-5 w-5 accent-[color:var(--mano-primary)]"
                      />
                      <span className="text-sm">I consent to this session being recorded.</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            <label className="mt-5 flex items-start gap-3 p-4 rounded-xl border border-[color:var(--mano-border)] cursor-pointer">
              <input
                data-testid="session-consent"
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 h-5 w-5 accent-[color:var(--mano-primary)]"
              />
              <span className="text-sm">
                I understand and consent to the terms above. I am ready to join the session.
              </span>
            </label>

            {isSpecialist && (
              <div className="mt-4 text-xs text-[color:var(--mano-ink-soft)]">
                {booking.recording_requested
                  ? booking.recording_patient_consent
                    ? "Patient has consented to recording. You can start recording from inside the session."
                    : "Recording is requested but the patient hasn't consented yet. Ask them to open the join link."
                  : "Recording is not enabled for this session. Enable it from your dashboard first."}
              </div>
            )}

            <button
              data-testid="session-join"
              onClick={requestJoin}
              disabled={!consent || loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white font-semibold transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Video size={16} />}
              {loading ? "Connecting…" : "Join the session"}
            </button>
            <div className="text-xs text-[color:var(--mano-ink-soft)] text-center mt-3">
              Powered by Jitsi Meet · Recording is captured on the specialist's device
            </div>
          </div>
        </div>
      </div>

      {/* Jitsi container */}
      <div className={joined ? "relative block" : "hidden"} style={{ height: "calc(100vh - 65px)" }}>
        <div ref={containerRef} data-testid="jitsi-container" className="w-full h-full bg-black" />

        {/* Doctor-only recording overlay */}
        {isSpecialist && booking.recording_requested && (
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2 bg-white/95 backdrop-blur border border-[color:var(--mano-border)] rounded-full pl-4 pr-2 py-1.5 shadow-lg">
            {recStatus === "recording" ? (
              <>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[color:var(--mano-high)]">
                  <span className="h-2 w-2 rounded-full bg-[color:var(--mano-high)] rec-pulse" /> REC
                </span>
                <button
                  data-testid="rec-stop"
                  onClick={stopRecording}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[color:var(--mano-high)] text-white text-xs font-semibold"
                >
                  <Square size={12} fill="currentColor" /> Stop
                </button>
              </>
            ) : recStatus === "uploading" ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[color:var(--mano-ink-soft)]">
                <Loader2 size={12} className="animate-spin" /> Uploading recording…
              </span>
            ) : recStatus === "done" ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[color:var(--mano-normal)]">
                <Upload size={12} /> Saved
              </span>
            ) : (
              <>
                <span className="uppercase-label">Consent {booking.recording_patient_consent ? "✓" : "…"}</span>
                <button
                  data-testid="rec-start"
                  onClick={startRecording}
                  disabled={!booking.recording_patient_consent}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[color:var(--mano-high)] text-white text-xs font-semibold disabled:opacity-50"
                >
                  <Radio size={12} /> Start recording
                </button>
              </>
            )}
          </div>
        )}

        {/* Patient-side "REC" indicator when doctor is recording */}
        {!isSpecialist && booking.recording_requested && booking.recording_patient_consent && (
          <div
            data-testid="patient-rec-indicator"
            className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 bg-black/70 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur"
          >
            <span className="h-2 w-2 rounded-full bg-[color:var(--mano-high)] rec-pulse" /> Recording is enabled for this session
          </div>
        )}
      </div>
    </div>
  );
}
